<template>
  <div class="waterfall-container">
    <div class="waterfall-wrapper" 
         @touchstart="touchStart"
         @touchmove="touchMove"
         @touchend="touchEnd"
         :style="{ transform: `translateY(${translateY}px)` }">
      <div class="refresh-tip" :class="{ refreshing: isRefreshing }">
        {{ refreshText }}
      </div>
      <div class="waterfall-content">
        <div class="column">
          <div v-for="item in leftList" 
               :key="item.id" 
               class="feed-item">
            <div v-if="item.type === 'video'" class="video-wrapper">
              <video-player class="video-player vjs-custom-theme"
                           :options="{
                             sources: [{ src: item.videoUrl, type: 'video/mp4' }],
                             poster: item.coverUrl,
                             controls: true,
                             preload: 'metadata',
                             playbackRates: [0.5, 1.0, 1.5, 2.0],
                             fluid: true
                           }"
                           :playsinline="true" />
            </div>
            <img v-else :src="item.imageUrl" :alt="item.title">
            <div class="item-info">
              <h3>{{ item.title }}</h3>
              <p>{{ item.desc }}</p>
              <div class="item-stats">
                <span>👍 {{ item.likes }}</span>
                <span>💬 {{ item.comments }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="column">
          <div v-for="item in rightList" 
               :key="item.id" 
               class="feed-item">
            <div v-if="item.type === 'video'" class="video-wrapper">
              <video-player class="video-player vjs-custom-theme"
                           :options="{
                             sources: [{ src: item.videoUrl, type: 'video/mp4' }],
                             poster: item.coverUrl,
                             controls: true,
                             preload: 'metadata',
                             playbackRates: [0.5, 1.0, 1.5, 2.0],
                             fluid: true
                           }"
                           :playsinline="true" />
            </div>
            <img v-else :src="item.imageUrl" :alt="item.title">
            <div class="item-info">
              <h3>{{ item.title }}</h3>
              <p>{{ item.desc }}</p>
              <div class="item-stats">
                <span>👍 {{ item.likes }}</span>
                <span>💬 {{ item.comments }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="loading-tip" v-if="loading">加载中...</div>
      <div class="no-more" v-if="noMore">没有更多了</div>
    </div>
  </div>
</template>

<script>
import { getFeedList } from '@/api/feed'
import { videoPlayer } from 'vue-video-player'
import 'video.js/dist/video-js.css'

export default {
  name: 'WaterfallFeed',
  components: {
    videoPlayer
  },
  props: {
    type: {
      type: String,
      default: 'images'
    }
  },
  data() {
    return {
      list: [],
      page: 1,
      pageSize: 10,
      loading: false,
      noMore: false,
      startY: 0,
      translateY: 0,
      isRefreshing: false,
      refreshText: '下拉刷新'
    }
  },
  computed: {
    leftList() {
      return this.list.filter((_, index) => index % 2 === 0)
    },
    rightList() {
      return this.list.filter((_, index) => index % 2 === 1)
    }
  },
  mounted() {
    this.loadData()
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    async loadData(refresh = false) {
      if (this.loading || (this.noMore && !refresh)) return
      
      this.loading = true
      if (refresh) {
        this.page = 1
        this.noMore = false
      }

      try {
        const { data } = await getFeedList(this.type, {
          page: this.page,
          pageSize: this.pageSize
        })

        if (refresh) {
          this.list = data.data.list
        } else {
          this.list = [...this.list, ...data.data.list]
        }

        if (this.list.length >= data.data.total) {
          this.noMore = true
        }
        this.page++
      } catch (error) {
        console.error('加载数据失败:', error)
      } finally {
        this.loading = false
        if (this.isRefreshing) {
          this.isRefreshing = false
          this.translateY = 0
        }
      }
    },
    handleScroll() {
      const scrollHeight = document.documentElement.scrollHeight
      const scrollTop = document.documentElement.scrollTop
      const clientHeight = document.documentElement.clientHeight

      if (scrollTop + clientHeight >= scrollHeight - 100) {
        this.loadData()
      }
    },
    touchStart(e) {
      if (document.documentElement.scrollTop === 0) {
        this.startY = e.touches[0].pageY
      }
    },
    touchMove(e) {
      if (!this.startY) return
      
      const currentY = e.touches[0].pageY
      const distance = currentY - this.startY

      if (distance > 0) {
        e.preventDefault()
        this.translateY = Math.min(distance / 2, 60)
        this.refreshText = this.translateY >= 50 ? '释放刷新' : '下拉刷新'
      }
    },
    touchEnd() {
      if (this.translateY >= 50) {
        this.isRefreshing = true
        this.refreshText = '刷新中...'
        this.loadData(true)
      } else {
        this.translateY = 0
      }
      this.startY = 0
    }
  }
}
</script>

<style lang="scss" scoped>
.waterfall-container {
  padding: 10px;
  background: #f5f5f5;
  min-height: 100vh;
  
  .waterfall-wrapper {
    transition: transform 0.3s;
  }

  .refresh-tip {
    text-align: center;
    height: 40px;
    line-height: 40px;
    margin-top: -40px;
    color: #666;
  }

  .waterfall-content {
    display: flex;
    justify-content: space-between;
    
    .column {
      width: 48%;
    }
  }

  .feed-item {
    background: #fff;
    border-radius: 8px;
    margin-bottom: 10px;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    img {
      width: 100%;
      display: block;
    }

    .video-wrapper {
      width: 100%;
      position: relative;
      padding-top: 100%; // 1:1比例
      background: #fff;

      .video-player {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        margin-bottom: 0;
      }
    }

    .item-info {
      padding: 8px;
      margin-top: -4px; // 减少与视频区域的间距

      h3 {
        margin: 0 0 6px;
        font-size: 14px;
        line-height: 1.3;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      }

      p {
        margin: 0 0 6px;
        font-size: 12px;
        color: #666;
        line-height: 1.3;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      }

      .item-stats {
        display: flex;
        gap: 8px;
        font-size: 12px;
        color: #999;
      }
    }
  }

  .loading-tip, .no-more {
    text-align: center;
    padding: 10px;
    color: #999;
  }
}

/* 自定义视频播放器样式 */
::v-deep .video-js {
  background-color: #fff !important;

  &.vjs-custom-theme {
    .vjs-poster {
      background-size: cover;
      background-position: center;
    }

    .vjs-big-play-button {
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 2em;
      height: 2em;
      line-height: 2em;
      border-radius: 50%;
      border: none;
      background-color: rgba(255, 255, 255, 0.8);
      padding: 0;
      font-size: 1em;

      .vjs-icon-placeholder:before {
        color: #333;
        font-size: 1.2em;
        line-height: 1.7em;
      }
    }

    .vjs-control-bar {
      background-color: rgba(0, 0, 0, 0.6);
      height: 2.5em;

      .vjs-button {
        width: 2.2em;
      }

      .vjs-progress-control {
        .vjs-progress-holder {
          margin: 0;
          height: 0.3em;
        }
      }

      .vjs-time-control {
        padding: 0 0.3em;
        line-height: 2.5em;
      }

      .vjs-volume-panel {
        .vjs-volume-control {
          width: 3em;
        }
      }
    }

    &.vjs-paused .vjs-control-bar {
      opacity: 0;
      pointer-events: none;
    }

    &.vjs-playing .vjs-control-bar {
      opacity: 1;
      pointer-events: auto;
    }

    .vjs-tech {
      object-fit: cover;
    }

    // 移除默认的空白边距
    .vjs-text-track-display,
    .vjs-loading-spinner {
      margin: 0;
      padding: 0;
    }
  }
}

/* 移动端优化 */
@media (max-width: 768px) {
  ::v-deep .video-js.vjs-custom-theme {
    .vjs-big-play-button {
      width: 1.8em;
      height: 1.8em;
      line-height: 1.8em;

      .vjs-icon-placeholder:before {
        font-size: 1em;
        line-height: 1.8em;
      }
    }

    .vjs-control-bar {
      height: 2.5em;

      .vjs-button {
        width: 2em;
      }

      .vjs-time-control {
        line-height: 2.5em;
      }
    }
  }
}
</style> 